import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { requestAppeal } from "services/advocateComplaintService";
import { AdvocateComplaints, Prosecutor } from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_APPEAL_STATUS, APPEAL_TYPE } from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  onCompliantRefresh: any;
  defaultAppealType?: "inspection_to_court" | "inspection_to_council";
}

export default function AddAppealModal({
  isOpen,
  setIsOpen,
  complaintData,
  onCompliantRefresh,
  defaultAppealType,
}: ModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  // const [query, setQuery] = useState("");
  // const [showDropdown, setShowDropdown] = useState(false);
  // const [prosecutorsData, setProsecutorsData] = useState<Prosecutor[]>([]);

  const [formState, setFormState] = useState({
    memorandum_summary: "",
    appeal_type:
      defaultAppealType || APPEAL_TYPE.INSPECTION_PROSECUTOR_TO_COUNCIL_OFFICE,
    requesting_prosecutor: null as Prosecutor | null,
    discipline_case: null,
    previous_decision: null,
    files: [] as File[],
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // useEffect(() => {
  //   const loadProsecutors = async () => {
  //     try {
  //       const data = await fetchProsecutor();
  //       // setProsecutorsData(data);
  //       // setError("");
  //     } catch (err) {
  //       // setError("Failed to load prosecutors.");
  //     } finally {
  //       // setLoading(false);
  //     }
  //   };
  //   loadProsecutors();
  // }, []);

  // const filtered = prosecutorsData?.filter((p) =>
  //   `${p?.full_name} ${p?.working_id}`
  //     .toLowerCase()
  //     .includes(query.toLowerCase())
  // );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  // const handleSelect = (prosecutor: Prosecutor, name: string) => {
  //   setFormState((prevState) => ({
  //     ...prevState,
  //     [name]: prosecutor,
  //   }));
  //   // setQuery("");
  //   // setShowDropdown(false);
  // };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // if (!formState?.requesting_prosecutor?.uu_id) {
    //   showToast("Requester Prosecutor is required", "error");
    //   return;
    // }

    if (!complaintData?.uu_id) {
      showToast(t("complaintNotFound"), "error");
      return;
    }

    if (formState?.files?.length <= 0) {
      showToast(t("memorandumFileRequired"), "error");
      return;
    }

    const data = new FormData();
    data.append("memorandum_summary", formState.memorandum_summary);
    data.append("memorandum_document", formState?.files[0] || "");
    data.append("appeal_type", defaultAppealType || formState.appeal_type);
    data.append("appeal_status", ADVOCATE_APPEAL_STATUS.PENDING);
    // data.append(
    //   "appeal_feedback_status",
    //   ADVOCATE_APPEAL_FEEDBACK_TYPE.RESPOND_BACK
    // );
    // data.append(
    //   "requesting_prosecutor",
    //   formState.requesting_prosecutor?.uu_id?.toString() || ""
    // );
    data.append("requesting_prosecutor", user?.full_name || "");
    data.append("userid_requesting_prosecutor", user?.user_id);
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append("discipline_case", String(complaintData?.uu_id));
    data.append(
      "is_appeal_to_general_council",
      complaintData?.is_regional ? "true" : "false",
    );

    const isFormData = true;

    setIsSubmitLoading(true);
    try {
      await requestAppeal(data, isFormData);

      // if (resData) {
      //   await editComplaint({
      //     ...complaintData,
      //     status: COMPLIANT_STATUS.APPEALED,
      //   });
      // }

      onCompliantRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        memorandum_summary: "",
        appeal_type: APPEAL_TYPE.INSPECTION_PROSECUTOR_TO_COUNCIL_OFFICE,
        requesting_prosecutor: null as Prosecutor | null,
        discipline_case: null,
        previous_decision: null,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("uploadMemorandum")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          {!defaultAppealType && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                {t("appealType")}
              </label>
              <select
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                name="appeal_type"
                value={formState.appeal_type}
                onChange={handleChange}
              >
                {/* {Object.values(APPEAL_TYPE)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))} */}

                <option value={"inspection_to_council"}>
                  {t("appealFromInspectionToCouncil")}
                </option>
                <option value={"inspection_to_court"}>
                  {t("appealFromInspectionToCourt")}
                </option>
              </select>
            </div>
          )}

          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("memorandumSummary")}
            </label>
            <textarea
              required
              rows={6}
              name="memorandum_summary"
              value={formState.memorandum_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("memorandumDocument")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={isSubmitLoading}
        >
          {isSubmitLoading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("uploadMemorandum")
          )}
        </button>
      </form>
    </SideModal>
  );
}
